/**
 * Resolves the correct price entry for a package given a selected group key.
 *
 * Mirrors the backend `calculatePricing` package-tier selection:
 *   guests = upper end of the selectedPeopleGroupKey range (e.g. "6-10" → 10)
 *   match: prices.find(tier => guests >= tier.minPersons && guests <= tier.maxPersons)
 *
 * This contains-based lookup is intentionally different from an exact min/max
 * equality check: a package with a single "1-10" tier must match "6-10" selection
 * because the backend sends guests=10 and finds 1<=10<=10.
 *
 * Falls back to exact key equality for edge-case boundary scenarios where the
 * contains lookup yields nothing (should not happen in practice with valid data).
 */
export function resolvePackagePriceEntry(prices, groupKey) {
  if (!Array.isArray(prices) || prices.length === 0) return null;
  if (!groupKey) return prices[0] || null;

  const [minStr, maxStr] = String(groupKey).split('-');
  const keyMin = parseInt(minStr || '0', 10);
  const keyMax = maxStr ? parseInt(maxStr, 10) : keyMin;

  // Upper end of the range = what getBookingNumberOfGuests returns and what
  // the backend uses as `guests` to pick a tier.
  const guests = Number.isFinite(keyMax) && keyMax > 0 ? keyMax : keyMin;

  const parseTierBounds = (p) => ({
    pMin: typeof p.minPersons === 'number' ? p.minPersons : parseInt(p.minPersons || '0', 10),
    pMax: typeof p.maxPersons === 'number' ? p.maxPersons : parseInt(p.maxPersons || '0', 10),
  });

  // Primary: contains-based (backend-identical)
  if (guests > 0) {
    const contained = prices.find((p) => {
      const { pMin, pMax } = parseTierBounds(p);
      return guests >= pMin && guests <= pMax;
    });
    if (contained) return contained;
  }

  // Fallback: exact key equality
  return (
    prices.find((p) => {
      const { pMin, pMax } = parseTierBounds(p);
      return pMin === keyMin && pMax === keyMax;
    }) || null
  );
}
